import React, { useState } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { PAGE_SIZE } from 'constants/Common';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppSearch from '@tenant/core/AppSearchBar';
import useGetListDockerImage from 'redux/slices/dockerImage/hooks/useGetListDockerImage';
import DockerAppTypes from 'constants/DockerAppTypes';
import { useSelector } from 'react-redux';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import useModal from '@tenant/utility/hooks/useModal';
import { debounce, deepClone } from '@tenant/utility/helper/Utils';
import useDeleteDockerImage from 'redux/slices/dockerImage/hooks/useDeleteDockerImage';
import DeleteDockerImageAction from '../Common/DeleteDockerImageAction';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQueryClient } from '@tanstack/react-query';
import queryKeyDockerImage from 'redux/slices/dockerImage/constants/queryKeyDockerImage';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';

const DockerImageTable = () => {
  const nextTick = useNextTick();
  const queryClient = useQueryClient();
  const canDeploy = useAllowedAccess([AuthorizePermission.REPOSITORY_DEPLOY]);
  const canDelete = useAllowedAccess([AuthorizePermission.REPOSITORY_DELETE]);
  const { list, filter } = useSelector((s) => s.dockerImage);
  const [searchTerm, setSearchTerm] = useState(filter?.searchTerm ?? '');
  const [isSearchTermChanged, setIsSearchTermChanged] = useState(false);
  const [pageIndex, setPageIndex] = useState(
    filter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  );
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
  const { isLoading, isRefetching, refetch } = useGetListDockerImage({
    searchTerm,
    pageIndex,
    pageSize,
  });
  const totalCount = list?.TotalCount ?? 0;
  const navigate = useNavigate();
  const {
    visible: visibleDelete,
    onShow: onShowDelete,
    onClose: onCloseDelete,
  } = useModal();
  const deleteMutate = useDeleteDockerImage();
  const [selected, setSelected] = useState();
  const loading = isLoading || isRefetching;

  const refetchList = () => {
    isSearchTermChanged && refetch();
    setIsSearchTermChanged(false);
  };

  const debounceSetSelected = debounce((value) => {
    setSelected(value);
  }, 500);

  const clearSearchTerm = async () => {
    setSearchTerm('');
    await nextTick();
    refetch();
  };

  const onChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    setIsSearchTermChanged(true);
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const handleShowDelete = (item) => {
    setSelected(deepClone(item));
    onShowDelete();
  };

  const handleCloseDelete = () => {
    onCloseDelete();
    debounceSetSelected();
  };

  const columns = [
    {
      field: 'Name',
      headerName: 'Image Name',
      width: 450,
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Link
          to={ROUTE_STRING.DOCKER_IMAGE.VIEW.replace(
            ':repository',
            params.value,
          )}
        >
          {params.value}{' '}
        </Link>
      ),
    },
    {
      field: 'DeploymentType',
      headerName: 'Deployment Type',
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <Box
          component={'span'}
          sx={{ width: '100%', display: 'block' }}
          className='MuiDataGrid-cellContent'
        >
          {DockerAppTypes.find((e) => e.Value === params.value)?.Description}
        </Box>
      ),
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          sx={{ minWidth: 150 }}
          key='action--view'
          label='View'
          showInMenu
          onClick={() => {
            navigate(
              ROUTE_STRING.DOCKER_IMAGE.VIEW.replace(
                ':repository',
                params.row.Name,
              ),
            );
          }}
        />,
        <GridActionsCellItem
          sx={{ minWidth: 150 }}
          key='action--upgrade'
          label='Deployment New Version'
          showInMenu={canDeploy}
          onClick={() => {
            navigate(
              ROUTE_STRING.DOCKER_IMAGE.UPGRADE_VERSION.replace(
                ':repository',
                params.row.Name,
              ),
            );
          }}
        />,
        <GridActionsCellItem
          key='action--delete'
          label='Delete'
          showInMenu={canDelete}
          onClick={() => {
            handleShowDelete(params.row);
          }}
        />,
      ],
    },
  ];

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  const goToAdd = () => {
    navigate(ROUTE_STRING.DOCKER_IMAGE.ADD);
  };

  const refreshTableAction = () => {
    if (pageIndex === DEFAULT_PAGE_INDEX) {
      refetch();
      return;
    }

    queryClient.removeQueries(
      queryKeyDockerImage.getList(JSON.stringify(DEFAULT_PAGE_INDEX, pageSize)),
    );
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  return (
    <>
      <DeleteDockerImageAction
        open={visibleDelete}
        loading={deleteMutate.isPending}
        handleCloseDelete={handleCloseDelete}
        handleSubmitDelete={handleCloseDelete}
        selected={selected}
      />

      <AppsHeader>
        <Box
          flex={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <AppSearch
              isClearable
              searchValue={searchTerm}
              onClearSearch={clearSearchTerm}
              iconPosition='right'
              id='outlined-adornment-weight'
              size='small'
              overlap={false}
              onChange={onChangeSearch}
              onBlur={refetchList}
            ></AppSearch>
          </Box>
          <AllowedAccess
            permissionIds={[AuthorizePermission.REPOSITORY_CREATE]}
          >
            <Button variant='outlined' onClick={goToAdd}>
              Add New
            </Button>
          </AllowedAccess>
        </Box>
      </AppsHeader>

      <Box sx={{ width: '100%', height: 'auto' }}>
        <DataGrid
          hideFooter
          disableColumnSelector
          disableColumnMenu
          rows={list?.Items ?? []}
          columns={columns}
          getRowId={(row) => row.Name}
          loading={loading}
          autoHeight
        />
      </Box>

      <FooterWrapper className='footer'>
        <Box justifyContent={'center'} className='footerContainer'>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE) || 1}
            page={pageIndex}
            onChange={(e, v) => onPageChange(v)}
            showFirstButton
            showLastButton
          />
          <Typography>Total: {totalCount || 0}</Typography>
          <Tooltip title='Refresh'>
            <IconButton
              disabled={isRefetching}
              size='small'
              sx={{ ml: 2 }}
              onClick={refreshTableAction}
            >
              <RefreshIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      </FooterWrapper>
    </>
  );
};

export default DockerImageTable;
